import { DonationIntervals, PaymentMethods, ReceiverTypes } from 'api-graphql-types'
import { z } from 'zod'
import { DonationFormLayouts } from '../../buildInitialValues/DonationFormLayouts'
import { videoFields } from './videoFields'
import votingFields from './votingFields'
import isNonEeaIbanCountry from '@/donationPages/_dependencies/helpers/isNonEeaIbanCountry'
import { bottomLogoOptions } from '@/donationPages/_dependencies/operations/getDonationFormConfig/getDonationFormConfigSchema'
import { getZipCodeSchema, htmlColorString, numericString, optionalNumericString } from '@/schema'
import {
  cappedString255,
  cappedString64,
  multiLineText,
  requiredCappedString255,
  requiredCappedString64,
} from '@/schema/sanitizedText'
import type { Pretty } from '@/types'
export const MAX_AMOUNT_CENTS = 2000000

export const urlOverrides = z.object({
  background_color: htmlColorString.optional(),
  bottom_logo: bottomLogoOptions,
  color: htmlColorString.optional(),
  client_reference: cappedString64,
  need_id: optionalNumericString,
})

export const base = z
  .object({
    amount_cents: z.coerce.number().int().min(100).max(MAX_AMOUNT_CENTS),
    codonation_cents: z.coerce.number().int().min(0),
    default_amount_cents: z.number().int(),
    default_codonation_cents: z.number().int().optional().or(z.null()),
    display_name: cappedString255,
    interval: z.enum(DonationIntervals),
    message: multiLineText.optional(),
    newsletter: z.boolean(),
    receipt_desired: z.boolean(),
    receiver_id: numericString,
    receiver_type: z.enum(ReceiverTypes),
    show_amount: z.boolean(),
    show_codonation: z.boolean(),
    show_user: z.boolean(),
    stripe_token: z.string().optional(),
    tracking_via: cappedString64,
    _company_donation: z.boolean(),
    _custom_amount: z.boolean(),
    _default_payment_method: z.enum(PaymentMethods),
    _layout: z.enum(DonationFormLayouts),
    _stripe_iban_country: z.string().optional(),
    ...urlOverrides.shape,
  })
  .superRefine(({ codonation_cents, amount_cents }, ctx) => {
    if (codonation_cents > amount_cents) {
      ctx.addIssue({
        code: 'too_big',
        maximum: amount_cents,
        inclusive: true,
        path: ['codonation_cents'],
        type: 'number',
        origin: 'number',
      })
    }
  })

const zipCodePart = getZipCodeSchema({ countryAlpha2Key: 'country_alpha2', zipCodeKey: 'zip' })
const zipCodePartOptional = getZipCodeSchema({ countryAlpha2Key: 'country_alpha2', zipCodeKey: 'zip', optional: true })
const importedAddressPart = z.object({
  city: cappedString255.optional(),
  street: cappedString255.optional(),
  zip: cappedString255.optional(),
  country_alpha2: cappedString255.optional(),
})

export const restrictedStripeSepaAndCc = z.discriminatedUnion('payment_method', [
  z.object({
    payment_method: z.literal(PaymentMethods.StripeCc),
    _stripe_complete: z.literal(true),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.StripeSepaDebit),
    _stripe_complete: z.literal(true),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.StripeApplePay),
    _stripe_complete: z.boolean().optional(),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.StripeGooglePay),
    _stripe_complete: z.boolean().optional(),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.DirectDeposit),
    _stripe_complete: z.boolean().optional(),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.DummyPayment),
    _stripe_complete: z.boolean().optional(),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.Paypal),
    _stripe_complete: z.boolean().optional(),
  }),
  z.object({
    payment_method: z.literal(PaymentMethods.StripeWero),
    _stripe_complete: z.boolean().optional(),
  }),
])

// Kept as its own part (instead of a refine on the whole form) so it also runs when other fields are invalid,
// e.g. on the iframe's first step. The message is rendered by StripeSEPADebitFields, only the path matters here.
export const sepaIbanCountryPart = z
  .object({
    payment_method: z.enum(PaymentMethods),
    _stripe_iban_country: z.string().optional(),
  })
  .refine(
    ({ payment_method, _stripe_iban_country }) =>
      payment_method !== PaymentMethods.StripeSepaDebit || !isNonEeaIbanCountry(_stripe_iban_country),
    { path: ['_stripe_iban_country'] }
  )

const dataImport = z.discriminatedUnion('import_data_from_psp', [
  z.object({
    import_data_from_psp: z.literal(true),
    payment_method: z.literal(PaymentMethods.Paypal),
    interval: z.literal(DonationIntervals.Single),
    email: z.email().min(0).max(255).optional().or(z.literal('')),
    first_name: cappedString255,
    last_name: cappedString255,
  }),
  z.object({
    import_data_from_psp: z.literal(false),
    payment_method: z.enum(PaymentMethods),
    interval: z.enum(DonationIntervals),
    email: z.email().min(1).max(255),
    first_name: requiredCappedString255,
    last_name: requiredCappedString255,
  }),
])

export const addressPart = z.union([
  z
    .object({
      import_data_from_psp: z.literal(false).optional(),
      receipt_desired: z.literal(false),
      city: cappedString255,
      // ReactAutosuggest wants street to be always a string
      street: cappedString255,
    })
    .and(zipCodePartOptional),
  z
    .object({
      import_data_from_psp: z.literal(false).optional(),
      receipt_desired: z.literal(true),
      city: requiredCappedString255,
      street: requiredCappedString255,
    })
    .and(zipCodePart),
  z
    .object({
      import_data_from_psp: z.literal(true),
      receipt_desired: z.literal(true),
    })
    .and(importedAddressPart),
])

export const companyPart = z.discriminatedUnion('_company_donation', [
  z.object({
    company_name: cappedString255,
    _company_donation: z.literal(false),
  }),
  z.object({
    company_name: requiredCappedString255,
    _company_donation: z.literal(true),
  }),
])

const notDirectDeposit = z.custom<Exclude<PaymentMethods, typeof PaymentMethods.DirectDeposit>>(
  (value) => value !== PaymentMethods.DirectDeposit
)

export const paymentMethodAndInterval = z.discriminatedUnion('interval', [
  z.object({
    interval: z.literal(DonationIntervals.Single),
    payment_method: z.enum(PaymentMethods),
  }),
  z.object({
    interval: z.literal(DonationIntervals.Monthly),
    payment_method: notDirectDeposit,
  }),
  z.object({
    interval: z.literal(DonationIntervals.Yearly),
    payment_method: notDirectDeposit,
  }),
])

const iframeCaptchaPart = z
  .object({
    _layout: z.literal(DonationFormLayouts.IFrame),
    channel: z.literal('iframe'),
    captcha_solution: z.string().optional(),
    _captcha_error: z.string().optional(),
    _captcha_complete: z.boolean(),
  })
  .and(restrictedStripeSepaAndCc)

export const captchaPart = z.union([
  iframeCaptchaPart,
  z.object({
    _layout: z.literal(DonationFormLayouts.Platform),
    captcha_solution: z.string().min(1),
    channel: requiredCappedString64,
    _captcha_error: z.string().optional(),
    _captcha_complete: z.literal(true),
    payment_method: z.enum(PaymentMethods),
    _stripe_complete: z.boolean().optional(),
  }),
])

const donationFormSchema = base
  .and(paymentMethodAndInterval)
  .and(dataImport)
  .and(addressPart)
  .and(companyPart)
  .and(captchaPart)
  .and(sepaIbanCountryPart)
  .and(videoFields)
  .and(votingFields)

export type DonationFormValues = Pretty<z.infer<typeof donationFormSchema>>
export default donationFormSchema
